import * as Sentry from '@sentry/react';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  private handleGoToLogin = () => {
    if (typeof window !== 'undefined') {
      window.location.replace('/login');
    }
  };

  private handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="from-base-500 dark:from-content-700 via-base-100 dark:via-content-500 to-base-500 dark:to-content-700 flex min-h-screen items-center justify-center bg-gradient-to-tr px-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/25 bg-white/55 p-6 text-center shadow-xl backdrop-blur-xl dark:border-content-700/50 dark:bg-content-900/40">
            <div className="icon-[ph--warning-circle] mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-4 font-header text-2xl text-content-900 dark:text-content-100">
              Something went wrong
            </h1>
            <p className="mt-2 font-sans text-content-700 dark:text-content-300">
              An unexpected error occurred. Please reload the page or sign in again.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-full border border-white/35 bg-white/60 px-4 py-2 text-sm font-semibold text-content-800 transition-colors hover:bg-white/80 dark:border-content-600/70 dark:bg-content-800/60 dark:text-content-100 dark:hover:bg-content-800"
              >
                Reload page
              </button>
              <button
                type="button"
                onClick={this.handleGoToLogin}
                className="rounded-full border border-primary-300/60 bg-primary-100/80 px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100 dark:border-primary-700/60 dark:bg-primary-900/40 dark:text-primary-200 dark:hover:bg-primary-900/60"
              >
                Go to login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
